'use client'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { AlertCircle, Loader2, Phone } from 'lucide-react'

import { BookingSummary } from '@/components/booking-summary'
import { useBookingForm } from '@/features/booking/model/useBookingForm'
import {
    AdditionalServicesStep,
    ConfirmationStep,
    DateSelectionStep,
    PetInformationStep,
} from '@/features/booking/ui/BookingSteps'
import { useClientProfile } from '@/hooks/use-client-profile'
import { useAuth } from '@/shared/auth'
import { useGlobalPublicConfig } from '@/shared/hooks/use-global-config'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { Form } from '@/shared/ui/form'
import { LanguageSwitchButton } from '@/shared/ui/language-switch-button'
import { AdditionalService } from '@monorepo/functions/src/types/services'

export default function BookingPage() {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const userIdFromUrl = searchParams.get('userId')
    const userId = userIdFromUrl || user?.uid
    const type = searchParams.get('type')
    const { clientProfile: clientProfile } = useClientProfile(userId || '')
    const { data: globalConfig } = useGlobalPublicConfig()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [hasBackendError, setHasBackendError] = useState(false)
    console.log('userId', user)
    const { form, state, setState, addPet, removePet, handleSubmit, nextStep, prevStep } = useBookingForm({
        clientProfile: clientProfile,
        userIdFromUrl: userIdFromUrl,
    })
    const isDriverTimeManagedExternally = !user?.isStaff

    const handleServiceChange = (services: AdditionalService[]) => {
        form.setValue('services', services)
        const hasDriverService = services.some(service => service.type === 'driver')
        if (hasDriverService && isDriverTimeManagedExternally) {
            setState(prev => ({ ...prev, dateError: '' }))
        }
    }

    const watchedPets = form.watch('pets') || []

    const watchedServices = form.watch('services')

    const handleConfirmClick = async () => {
        setIsSubmitting(true)
        setHasBackendError(false)
        try {
            const reservationId = await handleSubmit()
            if (reservationId) {
                navigate(`/booking/confirmation/${reservationId}`)
            } else {
                setHasBackendError(true)
            }
        } catch (error) {
            setHasBackendError(true)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            <div className='container mx-auto max-w-4xl p-4'>
                <div className='absolute right-4 top-4 z-50 flex items-center gap-2'>
                    <Link to='/perfil'>
                        <Button
                            variant='outline'
                            size='sm'
                            className='flex h-[34px] items-center justify-center bg-accent px-3 font-semibold hover:bg-accent/80'
                        >
                            {t('booking.myReservations', 'Mis Reservas')}
                        </Button>
                    </Link>
                    <LanguageSwitchButton />
                </div>
                {type === 'budget' && (
                    <Alert variant='destructive' className='mb-6'>
                        <AlertCircle className='h-4 w-4' />
                        <div className='flex w-full items-center justify-between'>
                            <div>
                                <AlertTitle>Creando Presupuesto</AlertTitle>
                                <AlertDescription>
                                    Estás en el proceso de crear un presupuesto. Si deseas realizar una reserva, haz
                                    clic aquí.
                                </AlertDescription>
                            </div>
                            <Button
                                variant='outline'
                                className='bg-white hover:bg-white/90'
                                onClick={() => {
                                    const newParams = new URLSearchParams(searchParams)
                                    newParams.delete('type')
                                    window.location.search = newParams.toString()
                                }}
                            >
                                Crear Reserva
                            </Button>
                        </div>
                    </Alert>
                )}
                <img src='/dondersteen-logo.png' alt='Dondersteen Logo' className='mx-auto mb-8 mt-8 h-24' />
                <div className={`grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-8`}>
                    <div className='lg:col-span-2'>
                        <Form {...form}>
                            <div className='space-y-8'>
                                {state.currentStep === 1 ? (
                                    <PetInformationStep
                                        form={form}
                                        clientProfile={clientProfile}
                                        onAddPet={addPet}
                                        onRemovePet={removePet}
                                    />
                                ) : state.currentStep === 2 ? (
                                    <DateSelectionStep
                                        form={form}
                                        selectedDates={state.selectedDates}
                                        checkInTime={state.checkInTime}
                                        checkOutTime={state.checkOutTime}
                                        onDatesChange={(dates) => {
                                            if (dates) {
                                                setState(prev => ({
                                                    ...prev,
                                                    selectedDates: dates,
                                                    dateError: prev.checkInTime && prev.checkOutTime ? '' : prev.dateError,
                                                }))
                                                form.setValue('dates', dates)
                                            } else {
                                                setState(prev => ({
                                                    ...prev,
                                                    selectedDates: null,
                                                    dateError: '',
                                                }))
                                                form.setValue('dates', null)
                                            }
                                        }}
                                        onCheckInTimeChange={(time) => {
                                            setState(prev => ({
                                                ...prev,
                                                checkInTime: time,
                                                dateError: time && prev.checkOutTime ? '' : prev.dateError,
                                            }))
                                        }}
                                        onCheckOutTimeChange={(time) => {
                                            setState(prev => ({
                                                ...prev,
                                                checkOutTime: time,
                                                dateError: prev.checkInTime && time ? '' : prev.dateError,
                                            }))
                                        }}
                                        onServiceChange={handleServiceChange}
                                        dateError={state.dateError}
                                        isDriverTimeManagedExternally={isDriverTimeManagedExternally}
                                    />
                                ) : state.currentStep === 3 ? (
                                    <AdditionalServicesStep
                                        form={form}
                                        onServiceChange={handleServiceChange}
                                        groomingUnavailable={state.groomingUnavailable}
                                    />
                                ) : (
                                    <ConfirmationStep form={form} clientProfile={clientProfile} userIdFromUrl={userIdFromUrl} />
                                )}
                            </div>
                        </Form>
                    </div>

                    <div
                        className={`flex flex-col gap-4 ${state.currentStep === 1 && window.innerWidth >= 1024 ? 'lg:col-span-1' : 'lg:col-span-1'}`}
                    >
                        {state.currentStep !== 1 && (
                            <BookingSummary
                                pets={watchedPets}
                                dates={state.selectedDates}
                                services={watchedServices}
                                clientClassification={clientProfile?.classification}
                            />
                        )}

                        {state.formError && (
                            <>
                                <Alert variant='destructive'>
                                    <AlertCircle className='h-4 w-4' />
                                    <AlertTitle>Error</AlertTitle>
                                    <AlertDescription>{state.formError}</AlertDescription>
                                </Alert>
                            </>
                        )}

                        {hasBackendError && (
                            <Alert variant='default' className='border-yellow-500 bg-yellow-50'>
                                <Phone className='h-4 w-4 text-yellow-600' />
                                <AlertTitle className='text-yellow-800'>
                                    {t('booking.errors.banner.title', '¿Tienes problemas?')}
                                </AlertTitle>
                                <AlertDescription className='text-yellow-700'>
                                    {t(
                                        'booking.errors.banner.description',
                                        'Si estás teniendo algún problema, llámanos al {{phoneNumber}} y te ayudaremos con tu reserva.',
                                        {
                                            phoneNumber: globalConfig?.phoneNumber || '',
                                        },
                                    )}
                                </AlertDescription>
                            </Alert>
                        )}
                        {state.currentStep === 2 && state.dateError && (
                            <Alert variant='destructive'>
                                <AlertCircle className='h-4 w-4' />
                                <AlertTitle>Error</AlertTitle>
                                <AlertDescription>{state.dateError}</AlertDescription>
                            </Alert>
                        )}
                        <div className='flex justify-between space-x-4'>
                            {state.currentStep > 1 && (
                                <Button type='button' variant='outline' onClick={prevStep}>
                                    {t('booking.navigation.previous', 'Anterior')}
                                </Button>
                            )}
                            {state.currentStep < 4 ? (
                                <Button type='button' className='w-full' onClick={nextStep}>
                                    {t('booking.navigation.next', 'Siguiente')}
                                </Button>
                            ) : (
                                <Button
                                    type='button'
                                    className='w-full'
                                    disabled={isSubmitting}
                                    onClick={handleConfirmClick}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            {t('booking.navigation.confirming', 'Confirmando...')}
                                        </>
                                    ) : (
                                        t('booking.navigation.confirm', 'Confirmar reserva')
                                    )}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
